/**
 * Labels and helpers shared by the device flags screens, and the mapping
 * between a flag's scope and the device facet bar.
 */
import type { DeviceScope, FlagCheck, FlagOp, FlagTrigger } from '@/lib/device-flag.service';
import type { ShellJobDeviceFilter } from '@/lib/shell-job.service';

export const TRIGGER_LABEL: Record<FlagTrigger | 'manual', string> = {
  enrolled: 'Enrolled',
  online: 'Back online',
  booted: 'Booted',
  schedule: 'Schedule',
  manual: 'Manual',
};

export const OP_LABEL: Record<FlagOp, string> = {
  is_empty: 'is empty',
  not_empty: 'is not empty',
  equals: 'equals',
  not_equals: 'does not equal',
  contains: 'contains',
  not_contains: 'does not contain',
  matches: 'matches',
  not_matches: 'does not match',
  exit_nonzero: 'exit code is not 0',
};

export const FIELD_LABEL: Record<string, string> = {
  machine_id: 'Machine ID',
  serial_number: 'Serial number',
  number: 'Number',
  model: 'Model',
  type: 'Type',
  sub_type: 'Sub-type',
  os_version: 'OS version',
  agent_version: 'Agent version',
  rom_version: 'ROM version',
};

/** Ops whose rule compares against a value. */
export const opNeedsValue = (op: FlagOp) => op !== 'is_empty' && op !== 'not_empty' && op !== 'exit_nonzero';
/** Ops where "ignore case and ':'" applies, on by default. */
export const opNormalizes = (op: FlagOp) =>
  op === 'equals' || op === 'not_equals' || op === 'contains' || op === 'not_contains';

/** "cat /sys/class/net/eth0/address does not equal {machine_id}". */
export function checkSummary(c: FlagCheck): string {
  const subject = c.kind === 'field' ? (FIELD_LABEL[c.field ?? ''] ?? c.field) : c.command;
  return [subject, OP_LABEL[c.op], opNeedsValue(c.op) ? c.value : ''].filter(Boolean).join(' ');
}

export function triggerLabel(t: FlagTrigger | 'manual', scheduleHours?: number): string {
  return t === 'schedule' && scheduleHours ? `Every ${scheduleHours} h` : TRIGGER_LABEL[t];
}

/** A flag's scope as the device facet bar edits it. Tenants are picked separately. */
export function scopeToFilter(s: DeviceScope): ShellJobDeviceFilter {
  return {
    type: s.types,
    model: s.models,
    os_version: s.os_versions,
    agent_version: s.agent_versions,
    is_internal: s.is_internal,
  };
}

export function filterToScope(f: ShellJobDeviceFilter, tenantIds: string[]): DeviceScope {
  const list = (v?: string[]) => (v?.length ? v : undefined);
  return {
    tenant_ids: list(tenantIds),
    types: list(f.type),
    models: list(f.model),
    os_versions: list(f.os_version),
    agent_versions: list(f.agent_version),
    is_internal: f.is_internal,
  };
}
